/* Experience Baires — motion.js
   Editorial motion with GSAP 3 + ScrollTrigger + SplitText (self-hosted in /assets).
   Rules:
   - Content is fully visible without this file. Initial "hidden" states are only set by GSAP
     after it has loaded, so a failed or blocked script never hides content.
   - prefers-reduced-motion: nothing is animated (the listener also reverts on change).
   - Motion never blocks interaction and never hides essential information. */
(() => {
  'use strict';

  const EB = window.EB || {};
  const settings = EB.settings || {};
  const assets = EB.assets || {};
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!settings.motion || reduce.matches || !assets.gsap) return;

  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const INTENSITY = { subtle: 0.55, editorial: 1, cinematic: 1.35 }[settings.intensity] || 1;
  const BOOT_START = performance.now();

  const loadScript = (src) => new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });

  const needsMotion = () => document.querySelector('[data-reveal], [data-hero], [data-hero-title], [data-scrub-words], [data-stack], [data-parallax], [data-parallax-media], [data-timeline]');

  let ctx = null;
  let splits = [];

  const init = () => {
    const { gsap, ScrollTrigger, SplitText } = window;
    gsap.registerPlugin(ScrollTrigger);
    if (SplitText) gsap.registerPlugin(SplitText);
    document.documentElement.classList.add('js-motion');

    const ease = 'expo.out';
    const dist = (px) => px * INTENSITY;
    const dur = (s) => s * (0.85 + INTENSITY * 0.15);
    const parallaxOn = settings.parallax !== false && window.matchMedia('(min-width: 750px)').matches;
    const lateBoot = performance.now() - BOOT_START > 1400; // skip the intro if it would replay already-seen content

    ctx = gsap.context(() => {
      /* ---------- Hero intro ---------- */
      $$('[data-hero], .pdp, .plp, .post, .journal, .phero').forEach((hero) => {
        const title = hero.querySelector('[data-hero-title]');
        const reveals = $$('[data-hero-reveal]', hero);
        const media = hero.querySelector('[data-hero-media]');
        const inView = hero.getBoundingClientRect().top < window.innerHeight;
        if (!inView || lateBoot) return;
        const tl = gsap.timeline({ defaults: { ease } });
        if (media) tl.from(media, { scale: 1 + 0.12 * INTENSITY, duration: dur(2.2), ease: 'power3.out' }, 0);
        if (title && SplitText) {
          const split = SplitText.create(title, { type: 'lines', mask: 'lines', linesClass: 'split-line', autoSplit: true,
            onSplit: (self) => gsap.from(self.lines, { yPercent: 110, duration: dur(1.3), stagger: 0.09, ease, delay: 0.15 }) });
          splits.push(split);
        } else if (title) {
          tl.from(title, { y: dist(40), autoAlpha: 0, duration: dur(1.2) }, 0.15);
        }
        if (reveals.length) tl.from(reveals, { y: dist(24), autoAlpha: 0, duration: dur(1.1), stagger: 0.08, clearProps: 'transform' }, 0.45);
      });

      /* ---------- Hero media scroll parallax ---------- */
      if (parallaxOn) {
        $$('[data-hero] [data-hero-media], .phero [data-hero-media], .plp__hero [data-hero-media]').forEach((media) => {
          gsap.to(media, {
            yPercent: 12 * INTENSITY,
            ease: 'none',
            scrollTrigger: { trigger: media.parentElement, start: 'top top', end: 'bottom top', scrub: true }
          });
        });
      }

      /* ---------- Line reveals (headings) ---------- */
      if (SplitText) {
        $$('[data-reveal="lines"]').forEach((el) => {
          const split = SplitText.create(el, {
            type: 'lines', mask: 'lines', linesClass: 'split-line', autoSplit: true,
            onSplit: (self) => gsap.from(self.lines, {
              yPercent: 105, duration: dur(1.1), stagger: 0.08, ease,
              scrollTrigger: { trigger: el, start: 'top 88%', once: true }
            })
          });
          splits.push(split);
        });
      } else {
        $$('[data-reveal="lines"]').forEach((el) => gsap.from(el, { y: dist(30), autoAlpha: 0, duration: dur(1), ease, scrollTrigger: { trigger: el, start: 'top 88%', once: true } }));
      }

      /* ---------- Fades ---------- */
      $$('[data-reveal="fade"]').forEach((el) => {
        gsap.from(el, { y: dist(22), autoAlpha: 0, duration: dur(1), ease, clearProps: 'transform',
          scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
      });

      /* ---------- Cards (batched stagger) ---------- */
      const cards = $$('[data-reveal="card"]');
      if (cards.length) {
        gsap.set(cards, { y: dist(40), autoAlpha: 0 });
        ScrollTrigger.batch(cards, {
          start: 'top 92%',
          once: true,
          onEnter: (batch) => gsap.to(batch, { y: 0, autoAlpha: 1, duration: dur(1.1), stagger: 0.09, ease, clearProps: 'transform,visibility,opacity' })
        });
        // Anything already above the fold on load must never stay hidden
        ScrollTrigger.addEventListener('refresh', () => {
          cards.forEach((c) => { if (c.getBoundingClientRect().bottom < 0) gsap.set(c, { clearProps: 'all' }); });
        });
      }

      /* ---------- Image reveals (clip) ---------- */
      $$('[data-reveal="image"]').forEach((el) => {
        const img = el.querySelector('img, svg');
        const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
        tl.from(el, { clipPath: 'inset(12% 8% 12% 8% round 24px)', duration: dur(1.4), ease: 'expo.inOut', clearProps: 'clipPath' });
        if (img) tl.from(img, { scale: 1 + 0.18 * INTENSITY, duration: dur(1.8), ease: 'expo.out' }, 0);
      });

      /* ---------- CTA banner mask ---------- */
      $$('[data-reveal="mask"]').forEach((el) => {
        gsap.fromTo(el, { clipPath: 'inset(10% 10% 10% 10% round 28px)' }, {
          clipPath: 'inset(0% 0% 0% 0% round 0px)', ease: 'none',
          scrollTrigger: { trigger: el.parentElement, start: 'top 85%', end: 'top 25%', scrub: true }
        });
      });

      /* ---------- Parallax ---------- */
      if (parallaxOn) {
        $$('[data-parallax-media]').forEach((el) => {
          const img = el.querySelector('img, svg');
          if (!img) return;
          gsap.set(img, { scale: 1.14 });
          gsap.fromTo(img, { yPercent: -6 * INTENSITY }, {
            yPercent: 6 * INTENSITY, ease: 'none',
            scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
          });
        });
        $$('[data-parallax]').forEach((el) => {
          const speed = parseFloat(el.dataset.parallax) || 0.1;
          gsap.to(el, { yPercent: -100 * speed * INTENSITY, ease: 'none',
            scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true } });
        });
      }

      /* ---------- Manifesto word scrub ---------- */
      if (SplitText) {
        $$('[data-scrub-words]').forEach((el) => {
          const split = SplitText.create(el, { type: 'words', wordsClass: 'word' });
          splits.push(split);
          gsap.fromTo(split.words, { opacity: 0.18 }, {
            opacity: 1, stagger: 0.1, ease: 'none',
            scrollTrigger: { trigger: el, start: 'top 80%', end: 'bottom 45%', scrub: true }
          });
        });
      }

      /* ---------- Destination stack: outgoing panel recedes ---------- */
      $$('[data-stack]').forEach((stack) => {
        const panels = $$('[data-stack-panel]', stack);
        panels.forEach((panel, i) => {
          const next = panels[i + 1];
          if (!next) return;
          const media = panel.querySelector('[data-stack-media]');
          gsap.to(panel, {
            scale: 1 - 0.06 * INTENSITY, borderRadius: 24, ease: 'none',
            scrollTrigger: { trigger: next, start: 'top bottom', end: 'top top', scrub: true }
          });
          if (media) gsap.to(media, { opacity: 0.45, ease: 'none', scrollTrigger: { trigger: next, start: 'top bottom', end: 'top top', scrub: true } });
        });
        panels.forEach((panel) => {
          const content = panel.querySelector('.dest-panel__content');
          if (!content) return;
          gsap.from(content.children, { y: dist(50), autoAlpha: 0, stagger: 0.08, duration: dur(1.1), ease,
            scrollTrigger: { trigger: panel, start: 'top 60%', once: true } });
        });
      });

      /* ---------- Itinerary progress line ---------- */
      $$('[data-timeline]').forEach((tl) => {
        const line = document.createElement('span');
        line.className = 'timeline__progress';
        line.setAttribute('aria-hidden', 'true');
        tl.prepend(line);
        gsap.to(line, { scaleY: 1, ease: 'none', scrollTrigger: { trigger: tl, start: 'top 70%', end: 'bottom 60%', scrub: true } });
      });
    });

    // Recalculate after fonts and late images settle
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => ScrollTrigger.refresh());
    window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
  };

  const destroy = () => {
    splits.forEach((s) => s && s.revert && s.revert());
    splits = [];
    if (ctx) ctx.revert();
    ctx = null;
    document.documentElement.classList.remove('js-motion');
  };

  const start = async () => {
    if (!needsMotion()) return;
    try {
      if (!window.gsap) await loadScript(assets.gsap);
      await Promise.all([
        window.ScrollTrigger ? null : loadScript(assets.scrollTrigger),
        window.SplitText ? null : loadScript(assets.splitText).catch(() => null)
      ]);
      init();
    } catch (e) {
      destroy(); // content stays visible
    }
  };

  // Content added later (filters, recommendations): reveal it without animation hiding.
  document.addEventListener('eb:content', () => {
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });

  // Theme editor: rebuild motion when sections change.
  if (settings.designMode) {
    const rebuild = () => { destroy(); if (window.gsap && !reduce.matches) init(); };
    document.addEventListener('shopify:section:load', rebuild);
    document.addEventListener('shopify:section:unload', () => setTimeout(rebuild, 0));
    document.addEventListener('shopify:section:reorder', () => window.ScrollTrigger && window.ScrollTrigger.refresh());
  }

  // Respect a change of preference mid-session.
  reduce.addEventListener('change', () => { if (reduce.matches) destroy(); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
